import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { getSafeImageSrc } from '../../utils/imageUtils';
import AssistantOptionsMenu from "../../components/AssistantOptionsMenu";
import { CoOwnerAvatars } from '../../components/CoOwnerAvatar';

// Utility function for relative time
const getRelativeTime = (timestamp: string): { relative: string; full: string } => {
  const now = new Date();
  const date = new Date(timestamp);
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  
  // Full formatted date for tooltip
  const fullDate = date.toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  if (diffInSeconds < 60) {
    return { relative: 'Just now', full: fullDate };
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return { relative: `${minutes} minute${minutes > 1 ? 's' : ''} ago`, full: fullDate };
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return { relative: `${hours} hour${hours > 1 ? 's' : ''} ago`, full: fullDate };
  } else if (diffInSeconds < 604800) {
    const days = Math.floor(diffInSeconds / 86400);
    return { relative: `${days} day${days > 1 ? 's' : ''} ago`, full: fullDate };
  } else if (diffInSeconds < 2629746) {
    const weeks = Math.floor(diffInSeconds / 604800);
    return { relative: `${weeks} week${weeks > 1 ? 's' : ''} ago`, full: fullDate };
  } else if (diffInSeconds < 31556952) {
    const months = Math.floor(diffInSeconds / 2629746);
    return { relative: `${months} month${months > 1 ? 's' : ''} ago`, full: fullDate };
  } else {
    const years = Math.floor(diffInSeconds / 31556952);
    return { relative: `${years} year${years > 1 ? 's' : ''} ago`, full: fullDate };
  }
};

const VirtualAssistantGridView = ({
  assistants,
  showNote,
  handleAssistantClick,
  showOptions,
  setShowOptions,
  optionsRef,
  handleCoOwner,
  handleCopyAppUrl,
  handleClone,
  handleExport,
  handleMergeClick,
  handleDelete,
  defaultAIFace
}: any) => {
  const [imageSrcs, setImageSrcs] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    const fetchImageSrc = async (assistant: any) => {
      const safeSrc = await getSafeImageSrc(assistant.app_ai_face || defaultAIFace);
      setImageSrcs((prev: { [key: string]: string }) => ({
        ...prev,
        [assistant.app_id]: safeSrc,
      }));
    };

    assistants.forEach((assistant: any) => fetchImageSrc(assistant));
  }, [assistants, defaultAIFace]);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {assistants.length > 0 ? (
          assistants.map((assistant: any) => {
            const timeData = getRelativeTime(assistant.last_modified);
            
            return (
              <div
                key={assistant.app_id}
                className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-200 p-6 cursor-pointer flex flex-col relative group"
                onClick={(e: any) => {
                  if (!(e.target as Element).closest(".options-button, .options-menu")) {
                    handleAssistantClick(assistant);
                  }
                }}
                onKeyDown={(e: any) => {
                  if (e.key === "Enter" || e.key === " ") {
                    handleAssistantClick(assistant);
                  }
                }}
                tabIndex={0}
                role="button"
                aria-label={`Open ${assistant.app_name} assistant`}
              >
                {/* Header Section - Icon + Name + Options */}
                <div className="flex items-start justify-between mb-4 gap-3">
                  <div className="flex items-center flex-1 min-w-0">
                    <div className="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center mr-3 flex-shrink-0 ring-1 ring-gray-200">
                      <img
                        src={imageSrcs[assistant.app_id] || defaultAIFace}
                        alt={`${assistant.app_name} icon`}
                        className="w-6 h-6 object-cover rounded-md"
                      />
                    </div>
                    <h3 className="font-semibold text-gray-900 flex-1 text-base leading-6 truncate pr-2">
                      {assistant.app_name.length > (showNote ? 50 : 50)
                        ? `${assistant.app_name.slice(0, showNote ? 50 : 50)}...`
                        : assistant.app_name}
                    </h3>
                  </div>

                  {/* Options Menu */}
                  <div className="relative flex-shrink-0">
                    <button
                      onClick={(e: any) => {
                        e.stopPropagation();
                        setShowOptions((prev: { [key: string]: boolean }) => ({
                          ...prev,
                          [assistant.app_id]: !prev[assistant.app_id],
                        }));
                      }}
                      className="text-gray-400 hover:text-gray-700 options-button p-2 rounded-full hover:bg-gray-100 transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-gray-300"
                      aria-label="Open options menu"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                      </svg>
                    </button>

                    {showOptions[assistant.app_id] && (
                      <div
                        className="absolute z-20 right-0 top-full mt-1 w-48 rounded-lg shadow-xl bg-white ring-1 ring-black ring-opacity-10 focus:outline-none border border-gray-100"
                        role="menu"
                        aria-orientation="vertical"
                        aria-labelledby="options-menu"
                      >
                        <AssistantOptionsMenu
                        assistant={assistant}
                        optionsRef={optionsRef}
                        handleCoOwner={handleCoOwner}
                        handleCopyAppUrl={handleCopyAppUrl}
                        handleClone={handleClone}
                        handleExport={handleExport}
                        handleMergeClick={handleMergeClick}
                        handleDelete={handleDelete}
                        type="virtual-list"
                      />
                      </div>
                    )}
                  </div>
                </div>

                {/* Description Section */}
                <div className="mb-6 flex-1">
                  {assistant.app_description ? (
                    <p className="text-sm text-gray-600 leading-relaxed line-clamp-3">
                      {assistant.app_description.length > (showNote ? 70 : 100)
                        ? `${assistant.app_description.slice(0, showNote ? 70 : 100)}...`
                        : assistant.app_description}
                    </p>
                  ) : (
                    <p className="text-sm text-gray-400 italic leading-relaxed">
                      No description available
                    </p>
                  )}
                </div>

                {/* Footer Section */}
                <div className="mt-auto pt-4 border-t border-gray-100">
                  <div className="grid grid-cols-2 gap-6 items-start">
                    {/* Left Side - Last Modified & Status */}
                    <div className="flex flex-col space-y-3">
                      {/* Last Modified */}
                      <div className="space-y-1">
                        <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Last Modified
                        </div>
                        <div 
                          className="text-sm text-gray-700 cursor-help relative group/tooltip font-medium"
                          title={timeData.full}
                        >
                          {timeData.relative}
                          {/* Tooltip */}
                          <div className="absolute bottom-full left-0 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover/tooltip:opacity-100 transition-opacity duration-200 whitespace-nowrap z-30 shadow-lg">
                            {timeData.full}
                            <div className="absolute top-full left-4 w-2 h-2 bg-gray-900 transform rotate-45 -mt-1"></div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Status */}
                      <div className="flex items-center gap-2">
                        {assistant.app_publishstatus.toLowerCase() === "published" ? (
                          <>
                            <FontAwesomeIcon 
                              icon={faCircleCheck} 
                              className="h-4 w-4 text-gray-700 flex-shrink-0" 
                            />
                            <span className="text-sm font-medium text-gray-800">
                              Published
                            </span>
                          </>
                        ) : (
                          <>
                            <FontAwesomeIcon 
                              icon={faEyeSlash} 
                              className="h-4 w-4 text-gray-500 flex-shrink-0" 
                            />
                            <span className="text-sm font-medium text-gray-600">
                              Unpublished
                            </span>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Right Side - Shared with */}
                    {Array.isArray(assistant.co_owner) && assistant.co_owner.length > 0 && (
                      <div className="flex flex-col items-end space-y-2">
                        <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Shared with
                        </div>
                        <div className="relative z-10">
                          <CoOwnerAvatars 
                            coOwners={assistant.co_owner} 
                            maxDisplay={3} 
                            onClick={() => handleCoOwner(assistant)}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        ): (
          <div className="col-span-full p-12 text-center">
            <div className="max-w-md mx-auto">
              <div className="text-gray-400 mb-3">
                <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <p className="text-gray-500 text-base leading-relaxed">
                No chatbots found in your profile or you have not created any chatbots yet.
              </p>
            </div>
          </div>
        )}
    </div>
  );
};

export default VirtualAssistantGridView;